class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Barkwoo: Escape closes the drawer even when a third-party overlay holds focus.
    this.onDocumentKeydown = (evt) => {
      if (evt.key === 'Escape' && this.classList.contains('active')) this.close();
    };
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // Delegated so it survives #CartDrawer re-renders (replaces Dawn's inline onclick close handlers).
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) this.close();
    });
    // Barkwoo: `toggle` doesn't bubble, so listen in the capture phase (also survives re-renders).
    this.addEventListener('toggle', this.onNoteToggle.bind(this), true);
    // Barkwoo: send the order note with Check out (as /cart does via form="cart"), so a note typed
    // just before checkout isn't lost to cart-note's debounced save. Added here rather than via a
    // form= attribute because the textarea is disabled while collapsed (see onNoteToggle).
    this.addEventListener('formdata', (event) => {
      const note = this.querySelector('#CartDrawer-Note');
      if (note && event.target.id === 'CartDrawer-Form') event.formData.set('note', note.value);
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    // Barkwoo (barkwoo-jum): drawer CSS loads non-blocking (media="print" swap). If the
    // drawer opens before a sheet's onload fired, apply it now.
    document.querySelectorAll('link[data-bw-cart-css][media="print"]').forEach((link) => {
      link.media = 'all';
    });
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        // Barkwoo: focus the heading ("Your cart (N)" / "Your cart is empty") so it is announced first.
        const focusElement = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty .cart__empty-text')
          : this.querySelector('.drawer__heading');
        trapFocus(containerToTrapFocusOn, focusElement || this.querySelector('.drawer__inner'));
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onDocumentKeydown);

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // Barkwoo resilience patch on Dawn 16: dispatchViewEvent is absent when
    // standard-events.js failed to load (see assets/cart.js), so optional-call it.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    document.removeEventListener('keydown', this.onDocumentKeydown);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  // Barkwoo: keep the focus trap in step with the order-note disclosure. Dawn's trapFocus() counts
  // any `textarea:enabled`, visible or not, so the textarea is rendered `disabled` while the note is
  // collapsed (snippets/cart-drawer.liquid). Enable it on open, disable it on close, then re-trap so
  // the trap's last stop is the textarea (open) or the summary (closed).
  onNoteToggle(event) {
    const details = event.target;
    if (!details.classList?.contains('bw-cart-note')) return;

    const note = details.querySelector('textarea');
    if (note) note.disabled = !details.open;
    if (!this.classList.contains('active')) return;

    const summary = details.querySelector('summary');
    trapFocus(document.getElementById('CartDrawer'), summary);
    // trapFocus() arms its Tab wrap on focusin, which doesn't fire for the already-focused summary
    // (the new last stop once the note closes), so replay it.
    summary.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.showAddedStatus();
      this.open();
    });
  }

  // Barkwoo: after an add, reveal the "Added to your cart." status inside the
  // freshly rendered drawer and announce it through the persistent live region
  // ([data-bw-cart-announce] sits outside #CartDrawer, so re-renders don't drop it).
  showAddedStatus() {
    const status = this.querySelector('[data-bw-cart-status]');
    if (!status) return;
    status.hidden = false;
    const announce = this.querySelector('[data-bw-cart-announce]');
    if (announce) {
      announce.textContent = '';
      setTimeout(() => {
        announce.textContent = (status.querySelector('span:last-child') || status).textContent.trim();
      }, 100);
    }
    document.dispatchEvent(new CustomEvent('barkwoo:cart:added', { detail: { drawer: this } }));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
